/**
 * Midway authentication utilities for Amazon internal SSO
 * Based on https://code.amazon.com/packages/MidwayIdentityCredentialProvider
 */

type QueryParameters = { [key: string]: string };

const midwayHost = 'midway-auth.amazon.com';

const buildQuery = (parameters: QueryParameters): string => {
  return Object.entries(parameters).reduce(
    (acc, [key, val]) => `${acc}${encodeURIComponent(key)}=${encodeURIComponent(val)}&`,
    '?',
  );
};

const generateNonce = (): string => {
  let nonce = '';
  const characterSet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

  for (let i = 0; i < 64; i += 1) {
    nonce += characterSet.charAt(Math.floor(Math.random() * characterSet.length));
  }

  return nonce;
};

const buildSSOUrl = (): string => {
  const queryParams: QueryParameters = {
    client_id: window.location.host,
    redirect_uri: window.location.href,
    response_type: 'id_token',
    scope: 'openid',
    nonce: generateNonce(),
  };

  return `https://${midwayHost}/SSO${buildQuery(queryParams)}`;
};

const buildSSORedirectUrl = (): string => {
  const queryParams: QueryParameters = {
    client_id: window.location.host,
    redirect_uri: window.location.href,
    response_type: 'id_token',
    scope: 'openid',
    nonce: generateNonce(),
  };

  return `https://${midwayHost}/SSO/redirect${buildQuery(queryParams)}`;
};

const getMidwayTokenFromUrl = (): string | null => {
  const tokenParam = 'id_token';

  const url = new URL(window.location.href);

  if (url.searchParams.has(tokenParam)) {
    const token = url.searchParams.get(tokenParam);

    url.searchParams.delete(tokenParam);
    window.history.replaceState({}, '', `${url.href}`);

    return token;
  }

  return null;
};

export interface MidwayUser {
  user_id: string;
  name?: string;
  given_name?: string;
  family_name?: string;
  email?: string;
}

export const getMidwayToken = (): Promise<string> => {
  const token = getMidwayTokenFromUrl();

  if (token) {
    return Promise.resolve(token);
  }

  return fetch(buildSSOUrl(), { credentials: 'include' }).then(async (response) => {
    if (response.ok) {
      return await response.text();
    } else {
      window.location.href = buildSSORedirectUrl();
      throw new Error('Cannot fetch token - redirecting to Midway login');
    }
  });
};

export function redirectToMidwayLogin(): void {
  window.location.href = buildSSORedirectUrl();
}
